<template>
	<v-card class="pb-4">
		<v-overlay
			:model-value="loading"
			class="d-flex align-center justify-center"
			contained
			persistent
			scrim
		>
			<v-progress-circular indeterminate color="primary" size="40" />
		</v-overlay>

		<v-toolbar color="primary" dark flat>
			<v-toolbar-title>{{ title }}</v-toolbar-title>
		</v-toolbar>

		<v-card-text>
			<v-text-field
				v-model="name"
				label="Measurement name"
				placeholder="e.g. Fasting glucose"
				density="compact"
				class="mb-2"
				autofocus
			/>

			<v-autocomplete
				v-model="preferredUnitId"
				:items="unitOptions"
				item-title="title"
				item-value="value"
				label="Preferred unit"
				hint="The Dactyl-canonical unit for this measurement"
				persistent-hint
				clearable
				density="compact"
				class="mb-4"
			>
				<template #item="{ props, item }">
					<v-list-item
						v-bind="props"
						:title="item.raw.title"
						:subtitle="item.raw.dimension"
					/>
				</template>
			</v-autocomplete>

			<v-text-field
				v-model="molarMass"
				label="Molar mass (g/mol)"
				type="number"
				min="0"
				step="any"
				hint="Optional. Enables mass <-> molar conversion for analytes."
				persistent-hint
				density="compact"
				class="mb-4"
			/>

			<p class="text-body-2 font-weight-medium mb-2">
				Aliases (Description surface forms)
			</p>
			<div class="mb-2">
				<div
					v-for="alias in aliases"
					:key="alias.uid"
					class="d-flex align-center mb-2"
				>
					<v-text-field
						v-model="alias.text"
						aria-label="Alias"
						class="flex-grow-1"
						density="compact"
						hide-details
					/>
					<v-icon
						class="ml-2 flex-shrink-0"
						color="grey-darken-1"
						:aria-label="'Remove alias'"
						@click="removeAliasRow(alias.uid)"
					>
						mdi-delete
					</v-icon>
				</div>
				<v-btn
					size="small"
					variant="text"
					color="secondary"
					prepend-icon="mdi-plus"
					@click="addAliasRow"
				>
					Add alias
				</v-btn>
			</div>
		</v-card-text>

		<v-card-actions>
			<v-spacer />
			<v-btn
				class="bg-secondary"
				elevation="2"
				:disabled="loading"
				@click="submit"
			>
				Save
			</v-btn>
			<v-btn class="bg-white" elevation="2" @click="close"> Cancel </v-btn>
			<v-spacer />
		</v-card-actions>
	</v-card>
</template>

<script>
import { mapActions } from "pinia";
import { useMainStore } from "@/store";

export default {
	name: "UnitEditor",
	props: {
		// "create" | "edit"
		mode: { type: String, required: true },
		initialData: { type: Object, default: () => ({}) },
		units: { type: Array, default: () => [] },
	},
	emits: ["dialog-close", "saved"],
	data() {
		return {
			loading: false,
			nextUid: 1,
			name: "",
			preferredUnitId: null,
			molarMass: "",
			aliases: [],
		};
	},
	computed: {
		title() {
			return this.mode === "edit"
				? "Edit measurement"
				: "New measurement";
		},
		unitOptions() {
			return this.units.map((u) => ({
				title: `${u.ucumCode} - ${u.label}`,
				value: u.id,
				dimension: u.dimension || "",
			}));
		},
	},
	created() {
		this.seedForm();
	},
	methods: {
		...mapActions(useMainStore, ["showNotification"]),
		seedForm() {
			const data = this.initialData || {};
			this.name = data.name || "";
			this.preferredUnitId = data.preferredUnitId ?? null;
			this.molarMass =
				data.molarMass === null || data.molarMass === undefined
					? ""
					: String(data.molarMass);
			this.aliases = (data.aliases || []).map((text) =>
				this.makeRow(text),
			);
		},
		makeRow(text = "") {
			return { uid: this.nextUid++, text };
		},
		addAliasRow() {
			this.aliases.push(this.makeRow());
		},
		removeAliasRow(uid) {
			this.aliases = this.aliases.filter((a) => a.uid !== uid);
		},
		cleanAliases() {
			return [
				...new Set(
					this.aliases
						.map((a) => a.text.trim())
						.filter(Boolean),
				),
			];
		},
		submit() {
			const name = this.name.trim();
			if (!name) {
				this.showNotification({
					status: "error",
					message: "Measurement name is required.",
				});
				return;
			}

			// Blank molar mass -> null; otherwise a positive number is required.
			let molarMass = null;
			const rawMass = String(this.molarMass).trim();
			if (rawMass !== "") {
				const parsed = Number(rawMass);
				if (!Number.isFinite(parsed) || parsed <= 0) {
					this.showNotification({
						status: "error",
						message:
							"Molar mass must be a positive number, or left blank.",
					});
					return;
				}
				molarMass = parsed;
			}

			const payload = {
				name,
				preferredUnitId: this.preferredUnitId ?? null,
				molarMass,
				aliases: this.cleanAliases(),
			};

			let request;
			if (this.mode === "edit") {
				request = this.$http.post("/dactyl-units/update", {
					id: this.initialData.id,
					...payload,
				});
			} else {
				request = this.$http.post("/dactyl-units/create", payload);
			}

			this.loading = true;
			request
				.then(() => {
					this.showNotification({
						status: "success",
						message: "Successfully saved preferred unit",
					});
					this.loading = false;
					this.$emit("saved");
				})
				.catch((error) => {
					const message =
						error?.response?.data?.message ??
						"A problem occurred while saving the preferred unit";
					this.showNotification({ status: "error", message });
					this.loading = false;
				});
		},
		close() {
			this.$emit("dialog-close");
		},
	},
};
</script>
